import React, { useState, Fragment } from 'react';
import CircularBarChart from '../../../Charts/CircularBarChart';

const QUARTERBACK_METRICS = [
	{ key: 'accuracy', label: 'Accuracy', field: 'accuracy_grade', appliesTo: 'quarterback' },
	{ key: 'mobility', label: 'Mobility', field: 'mobility_grade', appliesTo: 'quarterback' },
	{
		key: 'playmaking',
		label: 'Playmaking',
		field: 'quarterbacking_grade',
		appliesTo: 'quarterback'
	}
];

const STANDARD_METRICS = [
	{ key: 'sizeSpeed', label: 'Size/Speed', field: 'speed_grade', appliesTo: 'nonQuarterback' },
	{ key: 'explosiveness', label: 'Explosiveness', field: 'burst_grade', appliesTo: 'nonQuarterback' },
	{ key: 'agility', label: 'Agility', field: 'agility_grade', appliesTo: 'nonQuarterback' }
];

const ComparePlayerAthleticismScores = ({ selectedPlayers }) => {
	const hasQuarterback = selectedPlayers.some(
		(player) => player.position === 'quarterback'
	);
	const hasNonQuarterback = selectedPlayers.some(
		(player) => player.position !== 'quarterback'
	);
	const isMixedCompare = hasQuarterback && hasNonQuarterback;

	const metrics = isMixedCompare
		? [...QUARTERBACK_METRICS, ...STANDARD_METRICS]
		: hasQuarterback
			? QUARTERBACK_METRICS
			: STANDARD_METRICS;

	const [hoveredMetric, setHoveredMetric] = useState(null);

	const tooltipStyles = {
		position: 'absolute',
		top: '100%',
		left: '125px',
		transform: 'translateX(-50%)',
		whiteSpace: 'wrap',
		width: '250px'
	};

	const tooltipContent = {
		sizeSpeed:
			"This score is calculated based on the combination of the player's height, weight, and 40 time. It provides context to the raw 40 time by considering the player's size, resulting in a more accurate representation of their speed.",
		explosiveness:
			"This score is calculated based on a combination of the player's vertical jump and broad jump, which are widely accepted measures of a player's explosiveness.",
		agility:
			"This score is calculated based on a combination of the player's 3-cone drill and shuttle times, which are widely accepted measures of a player's lateral quickness and change of direction ability."
	};

	const getTooltip = (metric) => {
		if (metric.appliesTo === 'quarterback') {
			if (metric.key === 'accuracy') return tooltipContent.sizeSpeed;
			if (metric.key === 'mobility') return tooltipContent.explosiveness;
			return tooltipContent.agility;
		}
		return tooltipContent[metric.key];
	};

	const metricAppliesToPlayer = (player, metric) => {
		if (!player) return false;
		return metric.appliesTo === 'quarterback'
			? player.position === 'quarterback'
			: player.position !== 'quarterback';
	};

	const renderTooltip = (isHovered, content) =>
		isHovered && (
			<div
				className="p-2 bg-white border border-gray-300 shadow-lg rounded-lg text-sm z-10"
				style={tooltipStyles}
			>
				{content}
			</div>
		);

	return (
		<Fragment>
			{metrics.map((metric) => {
				const tooltip = getTooltip(metric);
				return (
					<tr key={metric.key}>
						<td className="px-6 whitespace-nowrap font-medium">
							{metric.label}
							<div className="relative inline-block">
								<i
									className={`fas fa-circle-info ml-2 ${hoveredMetric === metric.key ? 'text-gray-700' : ''}`}
									onMouseEnter={() => setHoveredMetric(metric.key)}
									onMouseLeave={() => setHoveredMetric(null)}
									style={{ marginLeft: '10px' }}
								/>
								{renderTooltip(hoveredMetric === metric.key, tooltip)}
							</div>
						</td>
						{selectedPlayers.map((player, index) => (
							<td key={`${metric.key}-${index}`} className="px-6 py-4">
								{metricAppliesToPlayer(player, metric) ? (
									<div style={{ marginLeft: '-45px' }}>
										<CircularBarChart
											title={metric.label}
											chartSize="xs"
											value={player[metric.field] || 0}
											maxPossible={10}
											infoIconText={tooltip}
											hasTitle={false}
											hasLabel={false}
											hasRankModalEnabled={false}
										/>
									</div>
								) : (
									'-'
								)}
							</td>
						))}
					</tr>
				);
			})}
		</Fragment>
	);
};

export default ComparePlayerAthleticismScores;
